import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  CircularProgress,
  ButtonGroup,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Chip,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import SaveIcon from "@mui/icons-material/Save";
import PrintIcon from "@mui/icons-material/Print";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LoadingButton from "@mui/lab/LoadingButton";
import LinkIcon from "@mui/icons-material/Link";
import VariantEdit from "../components/VariantEdit";
import { useSnackbar } from "notistack";
import { useStore } from "../authContext/StoreContext";
import api from "../hooks/apiClient";
import PinDialog from "../components/PinCodeDialog";
import ProductBarcodesDialog from "./ProductBarcodsPrint";
import ProductPriceTagDialog from "../components/PriceTagDialog";
const ProductDetails = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const {
    selectedStore,
    selectedDestStore,
    isStoreSelected,
    isDestStoreSelected,
  } = useStore();

  const [productData, setProductData] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [newInventoryValues, setNewInventoryValues] = useState({});
  const [inventoryMode, setInventoryMode] = useState(null); // 'fake' or 'real'
  const [loading, setLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [skuBarLoading, setSkuBarLoading] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetBtnLoading, setResetBtnLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openBarcodesD, setOpenBarcodesD] = useState(false);
  const [openPriceTag, setOpenPriceTag] = useState(false);
  const activeStoreId = isStoreSelected && selectedStore.id;
  const destStoreId = isDestStoreSelected && selectedDestStore.id;
  const [resetBtn, setResetBtn] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const fetchProductData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`products/find/${id}`);
      const product = response.data;

      const totalQuantity = product.variants.reduce(
        (acc, variant) => acc + variant.inventory_quantity,
        0
      );
      const totalShopify = product.variants.reduce(
        (acc, variant) => acc + variant.shopifyInventory,
        0
      );

      setProductData({ ...product, totalQuantity, totalShopify });
    } catch (error) {
      enqueueSnackbar("Failed to load product details", { variant: "error" });
      console.error("Error fetching product data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductData();
  }, [id]);
  const loadingBtn = () => {
    setSyncLoading(true);
  };
  const handleVariantEdit = (variant) => {
    setSelectedVariant(variant);
    setOpenEditDialog(true);
  };
  const handleInventoryChange = (var_id, value) => {
    setNewInventoryValues((prev) => ({
      ...prev,
      [var_id]: parseInt(value) || 0,
    }));
  };

  const handleSaveInventory = async () => {
    setLoading(true);
    try {
      const inventoryUpdates = Object.entries(newInventoryValues)
        .filter(([_, value]) => !isNaN(value))
        .map(([var_id, quantity]) => ({
          variant_id: var_id,
          new_quantity: quantity,
        }));

      if (inventoryUpdates.length === 0) {
        enqueueSnackbar("Blank inventory fields...", { variant: "warning" });
        return;
      }
      const response = await api.put(
        `inventory/adjust/${inventoryMode}/${id}`,
        {
          inventory_data: inventoryUpdates,
          store_id: activeStoreId,
        }
      );

      enqueueSnackbar("Inventory updated successfully", { variant: "success" });
      setNewInventoryValues({});
      setInventoryMode(null);
      fetchProductData();
    } catch (error) {
      enqueueSnackbar("Failed to update inventory", { variant: "error" });
      console.error("Error updating inventory:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMapProduct = async () => {
    setLoading(true);
    try {
      const response = await api.post(`products/single/map/${id}`, {
        client_store: destStoreId,
      });
      enqueueSnackbar(response.data.message, { variant: "success" });
      fetchProductData();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Failed to map product",
        {
          variant: "error",
        }
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncProduct = async () => {
    setLoading(true);
    try {
      // const response = await api.put(`products/single/syncUpdate/${id}`);
      // enqueueSnackbar(response.data.message, { variant: "success" });
      fetchProductData();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Failed to sync product",
        {
          variant: "error",
        }
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const getFunctionNameValue = async (btnValue) => {
    setOpenDialog(true);
    if (btnValue === "HARD_RESET") {
      setResetBtn(true);
    } else {
      setResetBtn(false);
    }
  };
  const generateSkuBar = async (pin) => {
    setSkuBarLoading(true);
    const someVariantsHaveSkuAndBarcode = productData.variants.some(
      (item) => item.sku && item.barcode
    );

    if (someVariantsHaveSkuAndBarcode) {
      enqueueSnackbar(`Alredy hav skus and barcodes .`, {
        variant: "warning",
      });
    }

    try {
      const response = await api.post(`products/generate/skubar/${id}`, {
        store: activeStoreId,
        pin: pin,
      });
      await fetchProductData();
      enqueueSnackbar(response.data.message, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(`Failed to generate sku product ${error.response.data}`, {
        variant: "error",
      });
      console.error(error);
    } finally {
      setSkuBarLoading(false);
    }
  };
  const hardResetBtn = async (pin_code) => {
    setResetBtnLoading(true);
    if (!pin_code) {
      setOpenDialog(true);
      return;
    }
    try {
      const response = await api.post(`products/hard/reset/${id}`, {
        store_id: activeStoreId,
        pin_code: pin_code,
      });
      await fetchProductData();
      enqueueSnackbar("Product Has been reseted...", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(`Failed to reset product ${error.response.data}`, {
        variant: "error",
      });
      console.error(error);
    } finally {
      setResetBtnLoading(false);
    }
  };

  const startInventoryEdit = (mode) => {
    setInventoryMode(mode);
    setNewInventoryValues({});
  };

  if (loading && !productData) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!productData) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load product details
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <VariantEdit
        ToggleOpen={() => setOpenEditDialog(false)}
        open={openEditDialog}
        variant={selectedVariant}
        onSave={fetchProductData}
      />
      <ProductBarcodesDialog
        ToggleOpen={() => setOpenBarcodesD(false)}
        open={openBarcodesD}
        product={productData}
      />
      <ProductPriceTagDialog
        ToggleOpen={() => setOpenPriceTag(false)}
        open={openPriceTag}
        product={productData}
      />
      <PinDialog
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        actionFunction={resetBtn ? hardResetBtn : generateSkuBar}
        dialogTitle={"Pin Code "}
        dialogContent={"Pin Is Must And Is Only Number"}
      />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          Confirm {inventoryMode === "fake" ? "Fake" : "Real"} Inventory Update
        </DialogTitle>
        <DialogContent>
          Are you sure you want to update{" "}
          {inventoryMode === "fake" ? "fake" : "real"} inventory?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              handleSaveInventory();
            }}
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
        {/* Product Image */}
        <Card sx={{ width: 300 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Media
            </Typography>
            <img
              src={productData.image_src}
              alt="Product"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: 200,
                objectFit: "contain",
              }}
            />
          </CardContent>
        </Card>

        {/* Product Info */}
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                <Chip
                  label={`Status: ${productData.status}`}
                  color={productData.status === "active" ? "success" : "error"}
                />
                {productData.laam_sync && <Chip label="Laam" color="warning" />}
                {productData.mapped &&
                  productData.mapped
                    .split(",")
                    .map((store, index) => (
                      <Chip
                        key={index}
                        label={store.trim()}
                        color="warning"
                        variant="outlined"
                        onClick={() => console.log(`Store: ${store}`)}
                      />
                    ))}
              </Box>

              <TextField
                fullWidth
                value={productData.title}
                variant="outlined"
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Button
                  variant={inventoryMode === "fake" ? "contained" : "outlined"}
                  color={inventoryMode === "fake" ? "success" : "inherit"}
                  onClick={() =>
                    inventoryMode === "fake"
                      ? setConfirmOpen(true)
                      : startInventoryEdit("fake")
                  }
                  disabled={loading}
                >
                  {inventoryMode === "fake"
                    ? "Save Fake Inventory"
                    : "Add Fake Inventory"}
                </Button>
                <Button
                  variant={inventoryMode === "real" ? "contained" : "outlined"}
                  color={inventoryMode === "real" ? "success" : "inherit"}
                  onClick={() =>
                    inventoryMode === "real"
                      ? setConfirmOpen(true)
                      : startInventoryEdit("real")
                  }
                  disabled={loading}
                >
                  {inventoryMode === "real"
                    ? "Save Inventory"
                    : "Add Inventory"}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Variants Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Variants
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr 1fr",
                  gap: 1,
                  alignItems: "center",
                  mb: 1,
                  fontWeight: "bold",
                }}
              >
                <Typography>Name</Typography>
                <Typography>SKU & BAR</Typography>
                <Typography align="right">Qty</Typography>
                {inventoryMode === "fake" && (
                  <Typography align="right">Fake Qty</Typography>
                )}
                {inventoryMode && (
                  <Typography align="right">New Qty</Typography>
                )}

                <Typography align="right">Shopify</Typography>
                <Typography align="right">Action</Typography>
              </Box>

              {productData.variants.map((variant) => (
                <Box
                  key={variant.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr 1fr",
                    gap: 1,
                    alignItems: "center",

                    py: 1,
                    borderBottom: "1px solid rgba(0,0,0,0.12)",
                  }}
                >
                  <Button
                    component={Link}
                    to={`/variant/${variant.sku}`}
                    startIcon={<LinkIcon />}
                    sx={{ justifyContent: "flex-start" }}
                  >
                    {variant.title}
                  </Button>

                  <Typography variant="caption">
                    {variant.sku}
                    <br />

                    {variant.barcode}
                  </Typography>

                  <Typography align="right">
                    {variant.inventory_quantity}
                  </Typography>
                  {inventoryMode === "fake" && (
                    <Typography align="right">
                      {variant.fake_inventory}
                    </Typography>
                  )}
                  {inventoryMode && (
                    <TextField
                      size="small"
                      type="number"
                      value={newInventoryValues[variant.id] || ""}
                      onChange={(e) =>
                        handleInventoryChange(variant.id, e.target.value)
                      }
                      sx={{ maxWidth: 200 }}
                    />
                  )}

                  <Typography align="right">
                    {variant.shopifyInventory}
                  </Typography>

                  <Button
                    size="small"
                    onClick={() => handleVariantEdit(variant)}
                    startIcon={<EditIcon />}
                  >
                    Edit
                  </Button>
                </Box>
              ))}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 2,
                  pt: 1,
                  borderTop: "1px solid rgba(0,0,0,0.12)",
                }}
              >
                <Typography>
                  Total Quantity: {productData.totalQuantity}
                </Typography>
                <Typography>
                  Shopify Quantity: {productData.totalShopify}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Actions Panel */}
        <Card sx={{ width: 250 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>

            <ButtonGroup orientation="vertical" fullWidth>
              <LoadingButton
                startIcon={<SyncIcon />}
                color="primary"
                loading={syncLoading}
                loadingPosition="start"
                onClick={handleSyncProduct}
                disabled={syncLoading}
              >
                Sync Product
              </LoadingButton>
              <LoadingButton
                color="primary"
                onClick={loadingBtn}
                loading={mapLoading}
                loadingPosition="start"
                startIcon={<SaveIcon />}
                disabled={
                  mapLoading ||
                  !isDestStoreSelected ||
                  (isStoreSelected && selectedStore.type === "destination")
                }
              >
                Map with {selectedDestStore?.public_name || "Store"}
              </LoadingButton>
              <LoadingButton
                color="primary"
                onClick={() => getFunctionNameValue("SKU_BAR")}
                loading={skuBarLoading}
                loadingPosition="start"
                startIcon={<SyncIcon />}
                disabled={
                  skuBarLoading ||
                  (isStoreSelected && selectedStore.type === "destination")
                }
              >
                Sku & barCodes
              </LoadingButton>
              <LoadingButton
                color="primary"
                onClick={() => getFunctionNameValue("HARD_RESET")}
                loading={resetBtnLoading}
                loadingPosition="start"
                startIcon={<SyncIcon />}
                disabled={
                  resetBtnLoading ||
                  (isStoreSelected && selectedStore.type === "destination")
                }
              >
                Hard Reset
              </LoadingButton>
              <LoadingButton
                color="primary"
                onClick={() => setOpenBarcodesD(true)}
                loading={loading}
                loadingPosition="start"
                startIcon={<PrintIcon />}
              >
                Print Barcodes
              </LoadingButton>
              <LoadingButton
                color="primary"
                onClick={() => setOpenPriceTag(true)}
                loading={loading}
                loadingPosition="start"
                startIcon={<LocalOfferIcon />}
              >
                Print Price Tags
              </LoadingButton>
            </ButtonGroup>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ProductDetails;

// import React, { useEffect, useState } from "react";
// import { useParams, Link } from "react-router-dom";

// import EditIcon from "@mui/icons-material/Edit";

// import VariantEdit from "../components/VariantEdit";
// import { CircularProgress, ButtonGroup, Button, Box } from "@mui/material";
// import { useSnackbar } from "notistack";
// import { useStore } from "../authContext/StoreContext";
// import api from "../hooks/apiClient";
// const ProductDetails = () => {
//   const { id } = useParams();
//   const {
//     selectedStore,
//     selectedDestStore,
//     isStoreSelected,
//     isDestStoreSelected,
//   } = useStore();

//   const { enqueueSnackbar } = useSnackbar();
//   const [productData, setProductData] = useState();

//   const [selectedVariant, setSelectedVariant] = useState({});
//   const [newInventoryValues, setNewInventoryValues] = useState({});
//   const [addFakeInv, setAddFakeInv] = useState(false);
//   const [addInv, setAddInv] = useState(false);
//   const [loading, setLoading] = useState(true);

//   const [open, setOpen] = useState(null);
//   const activeStoreId = isStoreSelected && selectedStore.id;
//   const destStoreId = isDestStoreSelected && selectedDestStore.id;

//   const ToggleOpen = (id) => {
//     if (id) {
//       const variant = productData.variants.find((variant) => variant.id === id);
//       setSelectedVariant(variant);
//       setOpen(true);
//     } else {
//       setSelectedVariant(null);
//       setOpen(false);
//     }
//   };

//   async function getData() {
//     try {
//       const response = await api.get(`products/find/${id}`);

//       const product = response.data;
//       // console.log(product);
//       const totalQuantity = product.variants.reduce(
//         (acc, variant) => acc + variant.inventory_quantity,
//         0
//       );
//       const totalShopify = product.variants.reduce(
//         (acc, variant) => acc + variant.shopifyInventory,
//         0
//       );

//       setProductData({ ...product, totalQuantity, totalShopify });

//       setLoading(false);
//     } catch (error) {
//       console.error("Error fetching product data:", error);
//       setLoading(false);
//     }
//   }
//   useEffect(() => {
//     getData();
//   }, [id]);

//   const MapThisProduct = async () => {
//     setLoading(true);
//     try {
//       const response = await api.post(`products/single/map/${id}`, {
//         client_store: destStoreId,
//       });
//       enqueueSnackbar(response.data.message, { variant: "success" });
//     } catch (error) {
//       enqueueSnackbar(error.response.data.message, { variant: "error" });
//       console.error(error);
//     } finally {
//       setLoading(false);
//     }
//   };
//   const handleInventoryChange = (inv_id, newValue) => {
//     setNewInventoryValues((prev) => ({
//       ...prev,
//       [inv_id]: newValue,
//     }));
//   };
//   const saveInventory = async (fake) => {
//     setLoading(true);
//     try {
//       const mapped = Object.entries(newInventoryValues).map(([key, value]) => ({
//         inventory_item_id: key,
//         quantity: value,
//       }));
//       const inventoryMap = Object.fromEntries(
//         mapped.map((item) => [item.inventory_item_id, item.quantity])
//       );

//       const updatedVariants = productData.variants.map((variant) => ({
//         ...variant,
//         new_quantity: inventoryMap[variant.inventory_item_id] ?? 0, // or 0 if you prefer
//       }));
//       const updatedProductData = { ...productData, variants: updatedVariants };
//       console.log(productData.variants);
//       // const response = await api.put(`inventory/adjust/${id}`, {
//       //  inventory_data:productData.variants,
//       //fake_status:fake,
//       // });

//       setNewInventoryValues({});
//       setTimeout(() => {
//         setAddFakeInv(false);
//         setAddInv(false);
//         getData();
//       }, 2000);
//       setLoading(false);
//     } catch (error) {
//       console.error("Error fetching product data:", error);
//       setLoading(false);
//     }
//   };
//   async function addFakeInvBtn() {
//     try {
//       if (addInv) {
//         const result = window.confirm(
//           "Are you sure you want to add fake inventory?"
//         );

//         if (result) {
//           // User clicked "OK"
//           console.log("User confirmed");
//         } else {
//           return;
//         }
//       }
//       setAddFakeInv(true);
//       setAddInv(false);
//     } catch (error) {}
//   }
//   async function addRealInvBtn() {
//     try {
//       if (setAddFakeInv) {
//         const result = window.confirm(
//           "Are you sure you want to add real inventory?"
//         );

//         if (result) {
//           // User clicked "OK"
//           console.log("User confirmed");
//         } else {
//           return;
//         }
//       }
//       setAddInv(true);
//       setAddFakeInv(false);
//     } catch (error) {}
//   }
//   if (loading || !productData) {
//     return (
//       <div>
//         {" "}
//         <CircularProgress />
//       </div>
//     );
//   }
//   async function SyncUpdateProduct() {
//     setLoading(true);
//     try {
//       const response = await api.put(`products/single/syncUpdate/${id}`);
//       enqueueSnackbar(response.data.message, { variant: "success" });
//     } catch (error) {
//       enqueueSnackbar(error.response.data.message, { variant: "error" });
//       console.error(error);
//     } finally {
//       setLoading(false);
//     }
//   }

//   return (
//     <div className="containers">
//       <VariantEdit
//         key={Date.now()}
//         ToggleOpen={ToggleOpen}
//         open={open}
//         variant={selectedVariant}
//       />
//       {/* <SentDeliveryDialoag
//         ToggleOpen={ToggleOpenDelivery}
//         open={open}
//         SendDelivery={() => SendDelivery()}
//       /> */}
//       <div className="shadow-sm p-3 mb-3 bg-body rounded d-flex flex-wrap d-flex justify-content-around">
//         <div className="shadow p-3 mb-3 bg-body rounded ">
//           <h4>Media</h4>
//           <img
//             src={productData.image_src}
//             className="card-img-top"
//             alt="Product"
//             height="200"
//           />
//         </div>
//         <div className="">
//           <div>
//             <span
//               className={`me-1 mb-1 btn btn-sm btn-${
//                 productData.status === "active" ? "info" : "danger"
//               }`}
//             >
//               Status: {productData.status}
//             </span>
//             {productData.laam_sync && (
//               <span type="button" className="btn btn-sm btn-warning me-1 mb-1">
//                 Laam
//               </span>
//             )}
//             <>
//               {productData.mapped &&
//                 productData.mapped.split(",").map((value, index) => (
//                   <span
//                     key={index}
//                     type="button"
//                     className="btn btn-sm btn-warning me-1 mb-1"
//                     onClick={() =>
//                       console.log(`Button clicked for value: ${value}`)
//                     }
//                   >
//                     {value.trim()}
//                   </span>
//                 ))}
//             </>
//           </div>
//           <div className="shadow-sm p-3 mb-3 bg-body rounded">
//             <input className="form-control" value={productData.title} />
//           </div>

//           <div className="shadow-sm p-3 mb-1 bg-body rounded d-flex  flex-column">
//             <div className="d-flex justify-content-evenly shadow-sm p-1 mb-1 bg-body rounded">
//               {!addFakeInv && (
//                 <button
//                   className="btn btn-sm btn-outline-dark"
//                   onClick={addFakeInvBtn}
//                 >
//                   Add Fake Inventory
//                 </button>
//               )}
//               {addFakeInv && (
//                 <button
//                   className="btn btn-sm btn-outline-success "
//                   onClick={() => saveInventory(true)}
//                 >
//                   Save Fake Inventory
//                 </button>
//               )}
//               {!addInv && (
//                 <button
//                   className="btn btn-sm btn-outline-dark "
//                   onClick={addRealInvBtn}
//                 >
//                   Add Inventory
//                 </button>
//               )}
//               {addInv && (
//                 <button
//                   className="btn btn-sm btn-outline-success "
//                   onClick={() => saveInventory(false)}
//                 >
//                   Save Inventory
//                 </button>
//               )}
//             </div>
//             <h5>Variants</h5>

//             <hr />

//             <div className="shadow-sm bg-body rounded d-flex gap-2 justify-content-around">
//               <p>Name</p>
//               <p>Qty</p>
//               {addFakeInv ? <p>FakeQty</p> : null}
//               {addInv ? <p>Real Inv</p> : null}
//               {addFakeInv ? <p>Add Fake Inv</p> : null}
//               <p>Action</p>
//             </div>
//             {productData.variants.map((variant, index) => (
//               <div
//                 className="p-1 d-flex justify-content-between"
//                 key={variant.id}
//               >
//                 <Link
//                   to={`/variant/${variant.sku}`}
//                   className="form-control p-1 btn btn-sm btn-link"
//                 >
//                   {" "}
//                   {variant.title} <i class="bi bi-chevron-right"></i>
//                 </Link>

//                 <div className="">
//                   <span className="fw-bold">{variant.shopifyInventory}</span>
//                   ..
//                   <span className="text-muted fontS">
//                     DB ({variant.inventory_quantity})
//                   </span>
//                 </div>
//                 {addFakeInv && (
//                   <div className="">
//                     <span className="fw-bold">{variant.fake_inventory}</span>

//                     <span className="text-muted fontS">
//                       Shopify ({variant.shopifyInventory})
//                     </span>
//                   </div>
//                 )}

//                 {addInv && (
//                   <input
//                     className="form-control p-1"
//                     type="number"
//                     value={newInventoryValues[variant.inventory_item_id] || ""}
//                     onChange={(e) =>
//                       handleInventoryChange(
//                         variant.inventory_item_id,
//                         parseInt(e.target.value)
//                       )
//                     }
//                   />
//                 )}
//                 {addFakeInv && (
//                   <input
//                     className="form-control p-1"
//                     type="number"
//                     value={newInventoryValues[variant.inventory_item_id] || ""}
//                     onChange={(e) =>
//                       handleInventoryChange(
//                         variant.inventory_item_id,
//                         parseInt(e.target.value)
//                       )
//                     }
//                   />
//                 )}
//                 <button
//                   className="form-control p-1 btn btn-sm btn-outline-info ms-1"
//                   onClick={() => ToggleOpen(variant.id)}
//                 >
//                   <EditIcon />
//                 </button>
//               </div>
//             ))}
//             <div className="shadow-sm p-1 bg-body rounded d-flex justify-content-around">
//               Quantity : {productData.totalQuantity}, Shop Quantity :{" "}
//               {productData.totalShopify}
//             </div>
//           </div>
//         </div>

//         <div className="shadow p-3 mb-3 bg-body rounded ">
//           <div className="d-flex  flex-column">
//             <span className="shadow-sm p-2 mb-1 bg-body rounded">
//               Mapped Details
//             </span>

//             <ButtonGroup color="warning" size="small" variant="outlined">
//               <Button onClick={() => SyncUpdateProduct()}>
//                 Sync this Product
//               </Button>
//               <Button
//                 onClick={() => MapThisProduct()}
//                 disabled={
//                   !isDestStoreSelected ||
//                   (isStoreSelected && selectedStore.type === "destination")
//                 }
//               >
//                 Map With
//                 {isDestStoreSelected && selectedDestStore.public_name}
//               </Button>
//             </ButtonGroup>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProductDetails;
